// Canned Responses API client & placeholder engine (WP-04).
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { stripUndefined } from './callable';
import type { LiveConversation } from './inboxTypes';

export interface CannedResponseDoc {
  id: string;
  tenantId: string;
  name: string;
  text: string;
  imagePaths: string[];
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

function call<TReq, TRes>(name: string) {
  const fn = httpsCallable<TReq, TRes>(functions, name);
  return async (data: TReq): Promise<TRes> => (await fn(stripUndefined(data))).data;
}

export const saveCannedResponse = call<
  { id?: string; name: string; text: string; imagePaths?: string[] },
  { ok: boolean; id: string }
>('cannedSave');

export const deleteCannedResponse = call<{ id: string }, { ok: boolean }>('cannedDelete');

export const listCannedResponses = call<Record<string, never>, { ok: boolean; items: CannedResponseDoc[] }>(
  'cannedList',
);

/**
 * Resolves standard placeholders: {{name}}, {{phone}}, {{date}}, {{time}}, {{custom:key}} or {{key}}
 */
export function resolveCannedPlaceholders(text: string, conv?: Partial<LiveConversation> | null): string {
  if (!text) return '';
  const now = new Date();
  const dateStr = now.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
  const timeStr = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return text.replace(/\{\{([a-zA-Z0-9_:\-]+)\}\}/g, (match, key: string) => {
    const lower = key.toLowerCase();
    if (lower === 'name') return conv?.displayName || conv?.name || '';
    if (lower === 'phone') return conv?.phone || '';
    if (lower === 'date') return dateStr;
    if (lower === 'time') return timeStr;

    // Check custom fields
    const customKey = lower.startsWith('custom:') ? lower.slice(7) : lower;
    if (conv?.customFields) {
      const matchField = conv.customFields.find((f) => f.name.toLowerCase() === customKey);
      if (matchField) return matchField.value;
    }
    return match;
  });
}
